import { Flex, Box, HStack, Icon, VStack, useDisclosure } from "@chakra-ui/react";
import CDS from "design-system";
import { useCallback, useMemo, useState } from "react";
import { LuLayoutGrid, LuList, LuEye, LuClock } from "react-icons/lu";
import { NewHireJourney } from "models/v2/newHireJourney";
import { Heading18 } from "components/Heading";
import TasksFiltersComponent, { TasksFilters } from "features/Resources/Task/Components/TasksFilters";
import { Task, TaskSummary } from "features/Resources/Task/models";
import { RelationType } from "models/v2/onboardingJourneyNetworkRelation";
import { filterTasks, isTaskOpen, journeyTaskAssigneeOptions, narrowListVisibility } from "features/Resources/Task/helpers";
import { computeTaskStats } from "features/Resources/Task/helpers/stats";
import { TaskCard, TasksByPersonView } from "features/Resources/Task/Components/UserTaskSummary";
import useSideDrawer from "hooks/useSideDrawer";
import ActionSideDrawer from "components/ActionSideDrawer";
import { SegmentedControl } from "design-system/components/SegmentedControl";
import { useCurrentUserQuery } from "features/User/hooks";
import { BasicUser, User } from "features/User/models";
import SendReminderModal from "features/SendMessage/Components/SendReminderModal";

import FilterSortSearchBar from "components/ControlBar/FilterSortSearchBar";
import FilterButton from "components/Button/FilterButton";

const defaultFilters: TasksFilters = {};

export interface TasksViewProps {
  newHireJourney: NewHireJourney;
  allTasks?: Task[];
  myTasks?: Task[];
  managerTasks?: Task[];
  newHireTasks?: Task[];
  teamTasks?: Task[];
}

type ViewMode = "person" | "list";

const VIEW_MODE_OPTIONS = [
  { value: "person" as const, label: "By Assignee", icon: LuLayoutGrid },
  { value: "list" as const, label: "List", icon: LuList },
];

export default function TasksView({
  newHireJourney,
  allTasks = [],
  myTasks = [],
  managerTasks = [],
  newHireTasks = [],
  teamTasks = [],
}: TasksViewProps) {
  const { data: currentUser } = useCurrentUserQuery();
  const [filters, setFilters] = useState<TasksFilters>(defaultFilters);
  const [viewMode, setViewMode] = useState<ViewMode>("person");
  const [includeNotStarted, setIncludeNotStarted] = useState(false);
  // Completed tasks are hidden by default; the "Show Completed" filter reveals them.
  const [hideClosed, setHideClosed] = useState(true);
  const [showCompletedList, setShowCompletedList] = useState(false);
  const [listSort, setListSort] = useState("effective_start_date_asc");

  // Remind modal state
  const [remindUser, setRemindUser] = useState<User | undefined>();
  const [remindTasks, setRemindTasks] = useState<Task[]>([]);
  const { isOpen: isRemindOpen, onOpen: onRemindOpen, onClose: onRemindClose } = useDisclosure();

  const { isOpen, onClose, selectedItem, expandClickHandlerByItem } = useSideDrawer<TaskSummary>({
    getItemId: (item) => item?.id,
  });

  const handleFiltersChange = useCallback((newFilters: TasksFilters) => {
    setFilters(newFilters);
  }, []);

  const handleReset = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  // Handle remind - open modal with user's tasks
  const handleRemind = useCallback(
    (user: User | BasicUser, userTasks: Task[]) => {
      const openTasks = userTasks.filter(isTaskOpen);
      setRemindUser(user as User);
      setRemindTasks(openTasks);
      onRemindOpen();
    },
    [onRemindOpen],
  );

  // Filter tasks by audience_filter selection
  const audienceFilteredTasks = useMemo(() => {
    if (!filters.audience_filter) {
      return allTasks;
    }

    switch (filters.audience_filter) {
      case "self":
        return myTasks;
      case "manager":
        return managerTasks;
      case "new-hire":
        return newHireTasks;
      case "team":
        return teamTasks;
      default:
        return allTasks;
    }
  }, [filters.audience_filter, allTasks, myTasks, managerTasks, newHireTasks, teamTasks]);

  // Build relational people list for TasksByPersonView. Assigned users are merged by id
  // (same person in two roles = one row); unassigned stakeholder slots are emitted with
  // no user so their role still surfaces (new hire always exists, so it's never a slot).
  const relationalPeople = useMemo<{ user: User | BasicUser | undefined; roles: RelationType[] }[]>(() => {
    const peopleMap = new Map<string, { user: User | BasicUser; roles: RelationType[] }>();
    const result: { user: User | BasicUser | undefined; roles: RelationType[] }[] = [];

    const stakeholders: { user: User | BasicUser | undefined; role: RelationType }[] = [
      { user: newHireJourney?.newHire, role: "new_hire" },
      { user: newHireJourney?.manager, role: "manager" },
      { user: newHireJourney?.buddy, role: "buddy" },
      { user: newHireJourney?.peopleTeamContact, role: "people_team_contact" },
      { user: newHireJourney?.recruiter, role: "recruiter" },
      { user: newHireJourney?.itTeamContact, role: "it_team_contact" },
    ];

    stakeholders.forEach(({ user, role }) => {
      if (!user) {
        if (role !== "new_hire") result.push({ user: undefined, roles: [role] });
        return;
      }
      const existing = peopleMap.get(user.id);
      if (existing) {
        existing.roles.push(role);
      } else {
        const entry = { user, roles: [role] };
        peopleMap.set(user.id, entry);
        result.push(entry);
      }
    });

    return result;
  }, [newHireJourney]);

  // Map status_filter to the format filterTasks expects
  const getStatusFilterType = useCallback((taskFilters: TasksFilters): string => {
    if (taskFilters.status && taskFilters.status.length > 0) {
      const hasCompleted = taskFilters.status.includes("completed");
      const hasSkipped = taskFilters.status.includes("skipped");
      const hasOpen = taskFilters.status.some((s) =>
        ["upcoming", "active", "in_progress", "blocked", "failed"].includes(s),
      );

      if (hasCompleted || hasSkipped) return "closed";
      if (hasOpen) return "open";
      return "all";
    }

    const statusFilter = taskFilters.status_filter || "all";
    if (statusFilter === "active") return "open";
    if (statusFilter === "closed") return "closed";
    if (statusFilter === "upcoming") return "open_with_upcoming";
    if (statusFilter === "overdue" || statusFilter === "due_soon") return "open";
    return "all";
  }, []);

  // Tasks for "List" view - apply full TasksFilters + sort
  const listViewTasks = useMemo(() => {
    const tasks = filterTasks(audienceFilteredTasks, {
      searchText: filters.q,
      statusFilter: getStatusFilterType(filters),
      assigneeId: filters.assignee_id,
      urgentOnly: filters.status_filter === "urgent",
      overdueOnly: filters.status_filter === "overdue",
      dueSoonOnly: filters.status_filter === "due_soon",
      hasIssues: filters.has_issues === "true",
      stageKey: filters.stage_key,
    });
    const filtered = narrowListVisibility(tasks, { includeNotStarted, showCompleted: showCompletedList });

    // Pre-key once per task before .sort() so we don't re-parse dates inside
    // the O(n log n) comparator. immediately = earliest (0), dependency = latest (Infinity).
    const toMs = (v: Date | string | null | undefined) => (v ? new Date(String(v)).getTime() : Infinity);
    const keys = new Map<string, { start: number; due: number }>();
    filtered.forEach((task) => {
      let start: number;
      if (task.effectiveStartDate) start = toMs(task.effectiveStartDate);
      else if (task.startImmediately) start = 0;
      else start = Infinity; // covers startAfterDependencyId and missing
      keys.set(String(task.id), { start, due: toMs(task.dueAt) });
    });
    const keyFor = (task: Task) => keys.get(String(task.id)) ?? { start: Infinity, due: Infinity };

    return [...filtered].sort((a, b) => {
      const ak = keyFor(a);
      const bk = keyFor(b);
      switch (listSort) {
        case "effective_start_date_asc": return ak.start - bk.start;
        case "effective_start_date_desc": return bk.start - ak.start;
        case "due_date_asc": return ak.due - bk.due;
        case "due_date_desc": return bk.due - ak.due;
        default: return 0;
      }
    });
  }, [audienceFilteredTasks, filters, getStatusFilterType, includeNotStarted, showCompletedList, listSort]);

  const handlePersonFilterReset = useCallback(() => {
    setIncludeNotStarted(false);
    setHideClosed(true);
  }, []);

  // Compute summary stats for the title
  const taskStats = useMemo(() => computeTaskStats(allTasks), [allTasks]);

  const summaryParts = useMemo(() => {
    const parts: string[] = [];
    if (taskStats.openCount > 0) parts.push(`${taskStats.openCount} open`);
    if (taskStats.overdueCount > 0) parts.push(`${taskStats.overdueCount} overdue`);
    if (taskStats.openAssigneeCount > 0) {
      parts.push(
        `${taskStats.openAssigneeCount} Stakeholder${taskStats.openAssigneeCount !== 1 ? "s" : ""} with Open Tasks`,
      );
    }
    return parts;
  }, [taskStats]);

  // Scope the List-view assignee filter to people who actually have tasks on
  // this journey, rather than the whole company user list.
  const journeyAssigneeOptions = useMemo(() => journeyTaskAssigneeOptions(allTasks), [allTasks]);

  const personViewFilterBar = useMemo(
    () => (
      <FilterSortSearchBar
        searchPlaceholder="Search tasks..."
        searchValue=""
        onSearchChange={() => {}}
        currentSort="default"
        defaultSort="default"
        onSortChange={() => {}}
        sortOptions={[]}
        hideSort
        quickFilters={
          <>
            <FilterButton
              onClick={() => setHideClosed((prev) => !prev)}
              isActive={!hideClosed}
              icon={<Icon as={LuEye} boxSize={4} />}
              size="sm"
            >
              Show Completed
            </FilterButton>
            <FilterButton
              onClick={() => setIncludeNotStarted((prev) => !prev)}
              isActive={includeNotStarted}
              icon={<Icon as={LuClock} boxSize={4} />}
              size="sm"
            >
              Show Not Started
            </FilterButton>
          </>
        }
        activeFilters={[]}
        onReset={handlePersonFilterReset}
        hasActiveFilters={false}
      />
    ),
    [includeNotStarted, hideClosed, handlePersonFilterReset],
  );

  return (
    <Flex direction="column" alignItems="flex-start" width="100%">
      {/* Title row: heading + toggle */}
      <HStack width="100%" justify="space-between" align="flex-start">
        <Box>
          <Heading18>Tasks</Heading18>
          {summaryParts.length > 0 && (
            <CDS.Text12 color="gray.500" mt={1}>
              {summaryParts.join(" \u00B7 ")}
            </CDS.Text12>
          )}
        </Box>
        <SegmentedControl options={VIEW_MODE_OPTIONS} value={viewMode} onChange={setViewMode} />
      </HStack>

      {/* Filter bar */}
      <Box width="100%" mt={3}>
        {viewMode === "person" ? (
          personViewFilterBar
        ) : (
          <TasksFiltersComponent
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onReset={handleReset}
            hideViewMode
            currentSort={listSort}
            onSortChange={setListSort}
            includeNotStarted={includeNotStarted}
            onToggleNotStarted={() => setIncludeNotStarted((prev) => !prev)}
            showCompleted={showCompletedList}
            onToggleShowCompleted={() => setShowCompletedList((prev) => !prev)}
            assigneeOptions={journeyAssigneeOptions}
          />
        )}
      </Box>

      {viewMode === "person" ? (
        <Box width="100%" mt={4}>
          <TasksByPersonView
            tasks={audienceFilteredTasks}
            currentUserId={currentUser?.id}
            includeNotStarted={includeNotStarted}
            hideClosed={hideClosed}
            relationalPeople={relationalPeople}
            newHireJourneyId={newHireJourney?.id}
            onRemind={handleRemind}
            onTaskClick={(task) => expandClickHandlerByItem(task)}
          />
        </Box>
      ) : (
        <VStack spacing={2} align="stretch" width="100%" mt={4}>
          {listViewTasks.map((task) => (
            <TaskCard key={task.id} task={task} compact onClick={(t) => expandClickHandlerByItem(t)} />
          ))}
        </VStack>
      )}

      <ActionSideDrawer isOpen={isOpen} onClose={onClose} prefixId={selectedItem?.prefixId} />

      {/* Remind Modal */}
      <SendReminderModal
        isOpen={isRemindOpen}
        onClose={onRemindClose}
        tasks={remindTasks}
        preselectedUser={remindUser}
        newHireJourneyId={newHireJourney?.id}
      />

    </Flex>
  );
}
